import { useMemo, useState } from "react";
import { isToday, startOfDay } from "date-fns";
import { Check, Loader2, Merge } from "lucide-react";
import toast from "react-hot-toast";
import { Order, OrderStatus } from "@/redux/services/order/order.type";
import {
  useGetOrdersQuery,
  useMergeOrdersMutation,
} from "@/redux/services/order/order.api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  cn,
  displayPrice,
  getOrderStatusText,
} from "@/lib/utils";
import { Button } from "../ui/button";

type MergeLine = { key: string; name: string; quantity: number };

/** Flattens an order's three line collections (items, combos, formulas) into a
 * short name+quantity list, so the picker can preview what would be folded in. */
function orderLines(order: Order): MergeLine[] {
  const lines: MergeLine[] = [];
  order.orderItems?.forEach((it) =>
    lines.push({
      key: it.id,
      name: it.menuItem?.title ?? it.dailyDish?.name ?? "Article",
      quantity: it.quantity,
    }),
  );
  order.menuComboOrderItems?.forEach((it) =>
    lines.push({
      key: it.id,
      name: it.menuCombo?.name ?? "Combo",
      quantity: it.quantity,
    }),
  );
  order.menuFormulaOrderItems?.forEach((it) =>
    lines.push({
      key: it.id,
      name: it.menuFormula?.name ?? "Formule",
      quantity: it.quantity,
    }),
  );
  return lines;
}

// Active dine-in statuses that can be merged — mirrors the API's
// MergeOrdersProcessor eligibility (SFRESTO-87).
const MERGEABLE_STATUSES: OrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
  "served",
];
const MERGEABLE_STATUS_PARAM = MERGEABLE_STATUSES.join(",");

interface MergeTableActionProps {
  order: Order;
  restaurantId: string;
  currency: string;
  /** Called after a successful merge (the current order may be the absorbed
   * one, so the caller typically closes the drawer). */
  onMerged: () => void;
}

/**
 * Lets a cashier merge the table's other active orders into this one, straight
 * from the order details panel — no need to switch the Commandes list into the
 * grouped-by-table view first (SFRESTO-87).
 */
function MergeTableAction({
  order,
  restaurantId,
  currency,
  onMerged,
}: MergeTableActionProps) {
  const [open, setOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [mergeOrders, { isLoading: isMerging }] = useMergeOrdersMutation();

  const tableCode = order.restaurantTable?.code;
  const isMergeable = MERGEABLE_STATUSES.includes(order.status);
  // Only offer merge for today's orders — merging is for the current table
  // session, not reconciling old history.
  const isOrderToday = isToday(new Date(order.createdAt));

  const { data } = useGetOrdersQuery(
    {
      restaurantId,
      query: {
        table: tableCode,
        status: MERGEABLE_STATUS_PARAM,
        "createdAt[after]": startOfDay(new Date()).toISOString(),
        limit: 100,
      },
    },
    { skip: !restaurantId || !tableCode || !isMergeable || !isOrderToday },
  );

  const otherOrders = useMemo(
    () =>
      (data?.results ?? [])
        .filter((o) => o.id !== order.id)
        // Most recent first, so the freshest orders on the table are on top.
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
    [data, order.id],
  );

  if (!isMergeable || !tableCode || !isOrderToday || otherOrders.length === 0) {
    return null;
  }

  const toggle = (id: string) =>
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleMerge = async () => {
    if (selectedIds.length === 0) return;
    try {
      await mergeOrders({ orderIds: [order.id, ...selectedIds] }).unwrap();
      toast.success("Commandes fusionnées");
      setOpen(false);
      setSelectedIds([]);
      onMerged();
    } catch {
      toast.error("Échec de la fusion des commandes");
    }
  };

  return (
    <>
      <Button
        variant="outline"
        className="w-full"
        onClick={() => setOpen(true)}
      >
        <Merge className="h-4 w-4 mr-2" />
        Fusionner avec la table ({otherOrders.length})
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Fusionner · Table {tableCode}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Sélectionnez les commandes à fusionner avec la commande #
            {order.orderNumber}.
          </p>
          <div className="mt-2 max-h-[50vh] space-y-2 overflow-y-auto">
            {otherOrders.map((o) => {
              const selected = selectedIds.includes(o.id);
              const lines = orderLines(o);
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => toggle(o.id)}
                  aria-pressed={selected}
                  className={cn(
                    "w-full rounded-lg border p-3 text-left transition-colors",
                    selected
                      ? "border-primary bg-orange-50"
                      : "border-border hover:bg-muted",
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-medium text-card-foreground">
                        #{o.orderNumber}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getOrderStatusText(o.status)}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <span className="tabular-nums font-medium text-card-foreground">
                        {displayPrice(o.totalPrice, currency)}
                      </span>
                      <span
                        className={cn(
                          "flex h-5 w-5 items-center justify-center rounded-full border-2",
                          selected
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border",
                        )}
                      >
                        {selected && <Check className="h-3.5 w-3.5" />}
                      </span>
                    </div>
                  </div>
                  <ul className="mt-2 space-y-0.5 border-t border-dashed border-border pt-2">
                    {lines.length === 0 ? (
                      <li className="text-xs text-muted-foreground">
                        Aucun article
                      </li>
                    ) : (
                      lines.map((line) => (
                        <li
                          key={line.key}
                          className="flex items-center justify-between gap-2 text-xs text-muted-foreground"
                        >
                          <span className="truncate">{line.name}</span>
                          <span className="shrink-0 tabular-nums">
                            x{line.quantity}
                          </span>
                        </li>
                      ))
                    )}
                  </ul>
                </button>
              );
            })}
          </div>
          <Button
            className="mt-2 w-full"
            disabled={selectedIds.length === 0 || isMerging}
            onClick={handleMerge}
          >
            {isMerging ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Merge className="h-4 w-4 mr-2" />
            )}
            Fusionner
            {selectedIds.length > 0 ? ` ${selectedIds.length + 1} commandes` : ""}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default MergeTableAction;
